import { motion } from 'motion/react';
import { Github, Linkedin, Mail, ChevronDown, Sparkles } from 'lucide-react';
import { ParticleBackground } from './ParticleBackground';

export function Hero() {
  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden px-6">
      {/* Particle Background */}
      <ParticleBackground />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-full mb-6"
          >
            <Sparkles className="text-cyan-400" size={16} />
            <span className="text-cyan-400 text-sm">Available for hire</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              Elite Full Stack Developer
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="mb-6"
          >
            <p className="text-slate-300 mb-2">Founder, CEO & Lead Developer</p>
            <p className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              ZeeJay Labs
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-slate-400 max-w-2xl mx-auto mb-8"
          >
            Architecting next-generation web experiences with cutting-edge technologies.
            Transforming ambitious ideas into scalable, high-performance digital solutions
            that drive business growth and user engagement.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="flex flex-wrap gap-3 justify-center mb-8 text-sm text-slate-400"
          >
            <span className="px-4 py-2 bg-slate-800/50 border border-cyan-500/20 rounded-full">React & Next.js</span>
            <span className="px-4 py-2 bg-slate-800/50 border border-cyan-500/20 rounded-full">TypeScript</span>
            <span className="px-4 py-2 bg-slate-800/50 border border-cyan-500/20 rounded-full">Node.js</span>
            <span className="px-4 py-2 bg-slate-800/50 border border-cyan-500/20 rounded-full">Cloud Architecture</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex gap-4 justify-center mb-12"
          >
            <a
              href="#projects"
              className="group px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all hover:scale-105 relative overflow-hidden"
            >
              <span className="relative z-10">View Projects</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <a
              href="#contact"
              className="px-8 py-3 border-2 border-cyan-500/50 rounded-lg hover:bg-cyan-500/10 transition-all hover:border-cyan-500 hover:scale-105"
            >
              Let's Talk
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="flex gap-6 justify-center"
          >
            <a
              href="https://github.com/Zenith201"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-slate-800/50 border border-cyan-500/20 rounded-lg text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all hover:scale-110"
            >
              <Github size={24} />
            </a>
            <a
              href="https://www.linkedin.com/in/zenith-takougoum"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-slate-800/50 border border-cyan-500/20 rounded-lg text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all hover:scale-110"
            >
              <Linkedin size={24} />
            </a>
            <a
              href="mailto:zenith.takougoum@zeejaylabs.dev"
              className="p-3 bg-slate-800/50 border border-cyan-500/20 rounded-lg text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all hover:scale-110"
            >
              <Mail size={24} />
            </a>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ delay: 1.2, y: { repeat: Infinity, duration: 2 } }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-400"
      >
        <ChevronDown size={32} />
      </motion.a>
    </section>
  );
}